import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { Campaign } from 'types';
import { isActivityTrivial } from 'utils/models/Activity';
import { CAMPAIGN_STATUSES, getFeatureForPhase } from 'utils/models/Campaign';
import {
  PERFORMANCE_ACCOMPLISHMENTS_MAXIMUM,
  PERFORMANCE_FEATURE_COMPLETE_RESUME_STEP_AI_DRAFT,
} from 'utils/models/PerformanceUtils';
import { useFeatures } from 'utils/util/features';
import ActivitiesAIBanner from './ActivitiesAIBanner';
import { ENHANCE_DRAFT_DESCRIPTORS } from './AssistedQuestionnaire/consts';
import {
  FAKE_IMPORT_ACTIVITIES_FROM,
  GENERIC_AUXILIARY_INPUT,
  getAiRequestType,
  TICKETING_SYSTEMS_INPUT,
  TICKETING_SYSTEMS_LOADING_INPUT,
  useAiBackgroundTask,
  useTicketingInformation,
} from './common';

// this number is completely made up, just so the system cannot be abused.
const DEFAULT_ACCOMPLISHMENTS_MAXIMUM_AI = 5;
const DEFAULT_EXISTING_ACTIVITIES = [];

const useToggleEnabled = ({ campaign }: { campaign: Campaign }) => {
  const { isGroupAndOptionEnabled } = useFeatures();
  const enabled = useMemo(
    () =>
      isGroupAndOptionEnabled(
        'ai_assistant',
        'ai_performance_self_activity_generator'
      ) &&
      getFeatureForPhase(
        campaign,
        'S',
        PERFORMANCE_FEATURE_COMPLETE_RESUME_STEP_AI_DRAFT
      ) !== false,
    [campaign, isGroupAndOptionEnabled]
  );

  const cannedResponses = useMemo(
    () => isGroupAndOptionEnabled('ai_assistant', 'ai_canned_demo_responses'),
    [isGroupAndOptionEnabled]
  );

  const value = useMemo(
    () => ({ enabled, cannedResponses }),
    [cannedResponses, enabled]
  );

  return value;
};

export const useAIActivityEnhance = ({
  campaign,
  contextPersonId,
  propsCallback,
  existingCurrentActivity,
  existingOtherActivities,
}) => {
  const { formatMessage } = useIntl();
  const { enabled, cannedResponses } = useToggleEnabled({ campaign });
  const { isGroupAndOptionEnabled } = useFeatures();

  const handleOnCallback = useCallback(
    (response: any) => {
      const activity = response?.SELF_ACTIVITY_ENHANCEMENT?.response;
      if (!activity) {
        toast.error(
          formatMessage({
            id: 'app.views.ai.activities.no_data.toast.text',
            defaultMessage:
              'AI did not return any data, you might try again. Adding more context might help with this.',
          })
        );
        return;
      }
      propsCallback({
        ...activity,
        isImport: false,
      });
    },
    [formatMessage, propsCallback]
  );

  const draft_data = useMemo(
    () => ({
      other_activities: existingOtherActivities,
      current_activity: existingCurrentActivity,
    }),
    [existingCurrentActivity, existingOtherActivities]
  );

  const { requestSuggestion: handleOnSubmit } = useAiBackgroundTask({
    suggestionType: 'SELF_ACTIVITY_ENHANCEMENT',
    requestParameters: {
      campaign_id: campaign?.id,
      draft_data,
      request_type: getAiRequestType(
        campaign?.status === CAMPAIGN_STATUSES.DEMO,
        cannedResponses
      ),
    },
  });

  const {
    ticketingSystems,
    loading: loadingTicketingSystems,
    fetch: fetchTicketingSystems,
  } = useTicketingInformation({
    campaign,
    contextPersonId,
    lazy: true,
  });

  const auxiliaryInput = useMemo(
    () =>
      enabled
        ? {
            ...GENERIC_AUXILIARY_INPUT(formatMessage, {
              onCallback: handleOnCallback,
              onSubmit: handleOnSubmit,
              onOpen: fetchTicketingSystems,
              additionalInputs: cannedResponses
                ? [FAKE_IMPORT_ACTIVITIES_FROM(formatMessage)]
                : loadingTicketingSystems
                  ? [TICKETING_SYSTEMS_LOADING_INPUT()]
                  : ticketingSystems.length > 0
                    ? [TICKETING_SYSTEMS_INPUT(formatMessage, ticketingSystems)]
                    : [],
              enableAttachments: isGroupAndOptionEnabled(
                'ai_assistant',
                'ai_enable_attachments'
              ),
            }),
            ...ENHANCE_DRAFT_DESCRIPTORS(formatMessage),
            buttonText: formatMessage({
              id: 'app.views.ai.activities.enhance_with_ai.button_text',
              defaultMessage: 'Enhance with AI ',
            }),
            type: 'MODAL',
            buttonStyle: {
              position: 'absolute',
              bottom: '0rem',
              left: '0.4rem',
            },
          }
        : undefined,
    [
      cannedResponses,
      enabled,
      formatMessage,
      handleOnCallback,
      handleOnSubmit,
      isGroupAndOptionEnabled,
      fetchTicketingSystems,
      loadingTicketingSystems,
      ticketingSystems,
    ]
  );

  const values = useMemo(
    () => ({ auxiliaryInput, enabled }),
    [auxiliaryInput, enabled]
  );
  return values;
};

export const useAIActivitiesGenerator = ({
  campaign,
  existingActivities = DEFAULT_EXISTING_ACTIVITIES,
  onCallback,
  contextPersonId,
}: {
  campaign: Campaign;
  existingActivities?: any[];
  onCallback: (object: any) => void;
  contextPersonId?: number;
}) => {
  const { enabled, cannedResponses } = useToggleEnabled({ campaign });

  const handleOnCallback = useCallback(
    (response) => {
      onCallback(response?.SELF_ACTIVITIES?.response);
    },
    [onCallback]
  );

  const nonTrivialActivities = useMemo(
    () => existingActivities.filter((activity) => !isActivityTrivial(activity)),
    [existingActivities]
  );

  const draft_data = useMemo(
    () => ({ activities: nonTrivialActivities }),
    [nonTrivialActivities]
  );

  const { requestSuggestion: handleOnSubmit } = useAiBackgroundTask({
    suggestionType: 'SELF_ACTIVITIES',
    requestParameters: {
      campaign_id: campaign?.id,
      draft_data,
      request_type: getAiRequestType(
        // @ts-expect-error
        campaign?.status === CAMPAIGN_STATUSES.DEMO,
        cannedResponses
      ),
    },
  });

  const maxAvailableAccomplishments = useMemo(
    () =>
      (getFeatureForPhase(campaign, 'S', PERFORMANCE_ACCOMPLISHMENTS_MAXIMUM) ||
        DEFAULT_ACCOMPLISHMENTS_MAXIMUM_AI) - nonTrivialActivities.length,

    [campaign, nonTrivialActivities.length]
  );

  const aiBanner = useMemo(
    () =>
      maxAvailableAccomplishments > 0 ? (
        <ActivitiesAIBanner
          onSubmit={handleOnSubmit}
          onCallback={handleOnCallback}
          campaign={campaign}
          contextPersonId={contextPersonId}
        />
      ) : undefined,
    [
      maxAvailableAccomplishments,
      handleOnSubmit,
      handleOnCallback,
      campaign,
      contextPersonId,
    ]
  );

  const values = useMemo(() => ({ enabled, aiBanner }), [enabled, aiBanner]);
  return values;
};
